import { createTRPCClient, httpBatchLink } from '@trpc/client'
import type { AppRouter } from '@salun/web-server/types'
import { toast } from '@salun/ui'
import { clearMe } from './auth'
import { reportTrpcError } from './errorReporter'

/**
 * Global session-expiry handler. When any tRPC call returns 401 the user's
 * session is dead — show a toast and bounce to the right login page based on
 * the URL audience (tenant / admin / public). Suppress duplicate firings so
 * a batch of 401s doesn't queue up multiple toasts/redirects.
 */
let sessionExpiredHandled = false
function handleSessionExpired(): void {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true
  clearMe()
  const path = window.location.pathname
  // Already on a login screen — don't bounce, don't toast (avoids loops on
  // failed login attempts that legitimately return 401).
  if (/\/login(\/|$)/.test(path)) {
    sessionExpiredHandled = false
    return
  }
  toast.error('Sessione scaduta. Effettua di nuovo il login.')
  let target = '/login'
  const tenantMatch = /^\/t\/([^/]+)/.exec(path)
  if (tenantMatch) target = `/t/${tenantMatch[1]}/login`
  else if (path.startsWith('/admin')) target = '/admin/login'
  else if (path.startsWith('/c/')) {
    const cMatch = /^\/c\/([^/]+)/.exec(path)
    if (cMatch) target = `/c/${cMatch[1]}/login`
  }
  // Slight delay so the toast renders before navigation tears down the tree.
  setTimeout(() => {
    window.location.replace(target)
  }, 250)
}

export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      fetch: async (url, opts) => {
        const res = await fetch(url, { ...opts, credentials: 'same-origin' })
        if (res.status === 401) handleSessionExpired()
        // Piggyback announcement probe: server sets this header when the
        // current tenant_user has at least one pending announcement. We fire
        // an event so the banner can refetch on demand without polling.
        // tRPC batch may run middleware N times → header collapsed as
        // "1, 1, 1". Treat any '1' token as pending.
        const pending = res.headers.get('x-announcements-pending')
        if (pending && pending.split(',').some((v) => v.trim() === '1') && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('salun:announcements-pending'))
        }
        // Server-side errors from tRPC procedures: 5xx OR JSON envelope with
        // error payload. We capture only true server faults (5xx) — 4xx are
        // expected user errors (validation, 401, 404) and not actionable.
        // The reportClientErrors path itself is excluded so a transient error
        // there doesn't loop.
        if (res.status >= 500 && !/\/trpc\/tracking\.reportClientErrors/.test(String(url))) {
          try {
            const cloned = res.clone()
            const text = await cloned.text()
            // tRPC 5xx body is JSON-encoded; truncate stack-like fields.
            reportTrpcError(`tRPC ${res.status}: ${String(url).slice(0, 200)}`, text.slice(0, 2000))
          } catch {
            reportTrpcError(`tRPC ${res.status}`, null)
          }
        }
        return res
      },
      headers: () => {
        const csrf = document.cookie
          .split('; ')
          .find((c) => c.startsWith('csrf_token='))
          ?.split('=')[1]
        return csrf ? { 'x-csrf-token': csrf } : {}
      },
    }),
  ],
})
